import type {
  ICaaOrder,
  ICaaTrackingInformation,
} from "../queries/customer-account-gql";
import type { IOrdersEdgesNode } from "../queries/customer-gql";
import type {
  AutomaticDiscountApplication,
  CartAutomaticDiscountAllocation,
  CartCodeDiscountAllocation,
  CartCustomDiscountAllocation,
  DiscountCodeApplication,
  MailingAddress,
  ManualDiscountApplication,
  Order,
  ScriptDiscountApplication,
} from "@shopify/hydrogen-react/storefront-api-types";
import type { IProductDetailQuery } from "../queries/product-gql";
import type { ILineItem, IReturnLineItem } from "../api/fetchOrder";
import { residentRegex } from "./regex";

export enum OrderStatusButtonType {
  CANCEL = "CANCEL", // 취소 버튼
  RETURN = "RETURN", // 반품 버튼
  NONE = "NONE", // 버튼 없음
}

// TODO: Partially -> Partially_Refunded 로 수정
// fulfillmentStatus
export enum OrderStatus {
  Refunded = "Refunded", // 환불 완료
  Canceled = "Canceled", // 주문 취소
  Partially = "Partially Refunded", // 부분 환불됨
  Completed = "Completed", // 주문 완료
  Return_Requested = "Return Requested", // 반품 접수됨
  Return_Approved = "Return Approved", // 반품 승인됨
  Shipped = "Shipped", // 발송 완료
  Partially_Shipped = "Partially Shipped", // 일부 발송 완료
  Partially_Preparing = "Partially Preparing", // 일부 배송 준비 중
  Preparing = "Preparing", // 배송 준비 중
  Scheduled = "ON HOLD", // 예약 발송 전
  Scheduled_Preparing = "Scheduled Preparing", // 예약 발송 준비 중
  Completed_Preparing = "Completed Preparing", // 예약 발송 완료
  PrizeCancel = "PrizeCancel", // 당첨 취소
  DrawCancel = "DrawCancel", // 응모 취소
  DrawLose = "DrawLose", // 미당첨
  DrawWin = "DrawWin", // 당첨 🎉
  DrawApply = "DrawApply", // 응모 완료
  Paid = "Paid", // 결제 완료 // TODO: 없애기
  Fulfilled = "Fulfilled",
  Unfulfilled = "Unfulfilled",
  Pending = "Payment Pending", // 결제 확인 중
  Gifting_Pending = "Gifting Pending", // 선물 수락 대기중
  Gifting_Accepted = "Gifting Accepted", // 선물 수락 완료
  None = "",
}

export const OrderStatusTrans: Record<OrderStatus, string> = {
  [OrderStatus.Refunded]: "order_status.refunded", // 환불 완료
  [OrderStatus.Canceled]: "order_status.canceled", // 주문 취소
  [OrderStatus.Partially]: "order_status.partially", // 부분 환불됨
  [OrderStatus.Completed]: "order_status.completed", // 결제 완료
  [OrderStatus.Return_Requested]: "order_status.return", // 반품 접수됨
  [OrderStatus.Return_Approved]: "order_status.return_approved", // 반품 승인됨
  [OrderStatus.Shipped]: "order_status.shipped", // 발송 완료
  [OrderStatus.Partially_Shipped]: "order_status.partially_shipped", // 일부 발송 완료
  [OrderStatus.Partially_Preparing]: "order_status.partially_shipped", // 일부 배송 준비 중 -> 패밀리세일용
  [OrderStatus.Preparing]: "order_status.preparing", // 배송 준비 중
  [OrderStatus.Scheduled]: "order_status.scheduled", // 예약 발송 전
  [OrderStatus.Scheduled_Preparing]: "order_status.scheduled_shipped", // 예약 발송 준비 중
  [OrderStatus.Completed_Preparing]: "order_status.completed_shipped", // 예약 발송 완료
  [OrderStatus.Paid]: "order_status.paid", // 결제 완료
  [OrderStatus.Fulfilled]: "order_status.fulfilled",
  [OrderStatus.Unfulfilled]: "order_status.unfulfilled",
  [OrderStatus.Pending]: "order_status.pending", // 결제 확인 중
  [OrderStatus.PrizeCancel]: "order_status.prizeCancel", // 당첨 취소
  [OrderStatus.DrawCancel]: "order_status.drawCancel", // 응모 취소
  [OrderStatus.DrawLose]: "order_status.drawLose", // 미당첨
  [OrderStatus.DrawWin]: "order_status.drawWin", // 당첨 🎉
  [OrderStatus.DrawApply]: "order_status.drawApply", // 응모 완료
  [OrderStatus.Gifting_Pending]: "order_status.gifting_pending", // 선물 수락 대기중
  [OrderStatus.Gifting_Accepted]: "order_status.gifting_accepted", // 선물 수락 완료
  [OrderStatus.None]: "", // 초기값
};

/** Draw Product를 포함하는 오더인지 확인 */
export const checkDrawOrder = (order?: IOrdersEdgesNode): boolean => {
  if (!order) return false;

  // Draw 태그가 있어도 Onsale 태그가 있으면 일반 상품으로 본다
  return Boolean(order.instagram) || Boolean(order.drawStatus);
};

/** 주문 목록 페이지에서 보여줘야 하는 주문만 필터 */
export const orderHistoryFilter = (order?: IOrdersEdgesNode): boolean => {
  if (!order) return false;
  if (order.isfamilysale) return false;

  // financialStatus가 PENDING인 주문은 보여주지 않는다.
  // Draw 오더이면서 financialStatus가 PAID가 아닌 주문은 보여주지 않는다.
  return !(
    order.financialStatus === "PENDING" ||
    (checkDrawOrder(order) &&
      order.financialStatus !== "PAID" &&
      order.financialStatus !== "REFUNDED" &&
      order.financialStatus !== "PARTIALLY_REFUNDED") ||
    order.drawStatus?.value === "lose"
  );
};

/** 주문이 송장번호를 가지고 있는지 확인 */
export const orderHasTracking = (order?: Order): boolean => {
  if (!order) return false;

  // order 데이터 아래에 successfulFulfillment와 trackingInfo가 있으면 송장이 있는 것으로 본다.
  return Boolean(order.successfulFulfillments?.[0]?.trackingInfo[0]);
};

/**
 * OrderHistory 페이지 주문상태
 * @param order - 주문 데이터
 */
export const getOrderStatus = (
  order: IOrdersEdgesNode,
): OrderStatus | undefined => {
  switch (order.financialStatus) {
    case "PAID":
      // 고객이 주문 완료 후 아직 비젬 주문 확정 전일 때
      if (order.fulfillmentStatus === "UNFULFILLED")
        return OrderStatus.Completed;
      else if (
        order.fulfillmentStatus === "FULFILLED" ||
        order.fulfillmentStatus === "PARTIALLY_FULFILLED"
      ) {
        // 고객이 주문 완료 후 Order History 페이지에서 반품 접수했을 때
        if (order.returnStatus?.value === "requested")
          return OrderStatus.Return_Requested;
        // CS팀에서 반품 승인했을 때
        if (order.returnStatus?.value === "approved")
          return OrderStatus.Return_Approved;

        // 주문에 프리오더 제품이 존재하고 해당 상품이 송장 전송이 되지 않았을 때
        if (hasPreorder(order) && !orderHasTracking(order)) {
          return OrderStatus.Scheduled_Preparing;
        }

        // 주문에 프리오더 제품이 존재하고 송장 전송까지 되었을 때
        if (hasPreorder(order) && orderHasTracking(order)) {
          return OrderStatus.Completed_Preparing;
        }

        // 고객이 주문 완료 후 비젬에서 송장 전송까지 했을 때
        if (orderHasTracking(order)) return OrderStatus.Shipped;

        // 고객이 주문 완료 후 비젬에서 주문 확정했을 때
        return OrderStatus.Preparing;
      }
      // 고객이 주문 완료 후 '일부 상품'을 hold 했을때
      else if (order.fulfillmentStatus === "ON_HOLD") {
        // 고객이 주문 완료 후 Order History 페이지에서 반품 접수했을 때
        if (order.returnStatus?.value === "requested")
          return OrderStatus.Return_Requested;

        // 일부 상품만 비젬에서 송장 전송까지 했을때
        if (orderHasTracking(order)) return OrderStatus.Partially_Shipped;

        // 고객이 주문 완료 후 비젬에서 주문 확정했을 때
        return OrderStatus.Paid;
      }
      return OrderStatus.Paid;
    case "REFUNDED":
      // 고객이 반품 신청 후, CS팀에서 승인한 뒤 환불 처리까지 완료했을 때
      if (!order.canceledAt) return OrderStatus.Refunded;
      // 고객 또는 CS팀에서 일반적으로 주문을 취소했을 때
      return OrderStatus.Canceled;
    case "PARTIALLY_REFUNDED":
      if (order.returnStatus?.value === "requested")
        return OrderStatus.Return_Requested;
      if (order.returnStatus?.value === "approved")
        return OrderStatus.Return_Approved;
      // 고객 또는 CS팀에서 일부 상품만 환불하거나 반품 처리했을 때
      return OrderStatus.Partially;
    default:
  }
};

/**
 * CAA 주문의 배송 추적 정보 (tracking number/url).
 * classic getTrackingInfo의 CAA 대응 — order.fulfillments[].trackingInformation으로 직접 제공됨
 * 가장 최근 fulfillment 기준으로 조회 (classic getTrackingInfo와 동일 규칙).
 */
export const getTrackingInfoCaa = (
  order?: ICaaOrder | null,
): ICaaTrackingInformation | null => {
  if (!order) return null;

  const fulfillment = order.fulfillments?.edges;
  const info =
    fulfillment?.[fulfillment.length - 1]?.node.trackingInformation[0];

  return info ?? null;
};

/** CAA 주문이 송장번호를 가지고 있는지 확인. classic orderHasTracking의 CAA 대응. */
export const orderHasTrackingCaa = (order?: ICaaOrder | null): boolean => {
  return Boolean(getTrackingInfoCaa(order));
};

export const getDiscountName = (
  discount?:
    | CartAutomaticDiscountAllocation
    | CartCodeDiscountAllocation
    | CartCustomDiscountAllocation
    | DiscountCodeApplication
    | ManualDiscountApplication
    | AutomaticDiscountApplication
    | ScriptDiscountApplication,
): string => {
  if (!discount) return "";

  if (
    discount.__typename === "CartAutomaticDiscountAllocation" ||
    discount.__typename === "CartCustomDiscountAllocation" ||
    discount.__typename === "ManualDiscountApplication" ||
    discount.__typename === "AutomaticDiscountApplication" ||
    discount.__typename === "ScriptDiscountApplication"
  ) {
    return discount.title;
  }

  if (
    discount.__typename === "CartCodeDiscountAllocation" ||
    discount.__typename === "DiscountCodeApplication"
  ) {
    return discount.code;
  }

  return "";
};

/** 주문한 상품중에 예약발송인 상품을 가지고 있는지 확인 */
export const hasPreorder = (order: IOrdersEdgesNode | null): boolean => {
  if (!order) return false;

  return order.lineItems.edges.some((edge) =>
    (
      edge.node.variant?.product as IProductDetailQuery["product"]
    )?.tags.includes("Preorder"),
  );
};

/**
 * TODO: 프리오더 기능 CAA 대응 시 구현.
 * hasPreorder의 CAA 대응 — 판별 기준은 동일하게 상품 태그에 "Preorder"가 있는지.
 * ICaaLineItem엔 classic의 variant.product.tags에 대응하는 필드가 없어(productId/productType만 존재)
 * CAA 응답만으로는 판별 불가 — productId로 Storefront product를 배치 조회해 tags를 붙인 뒤 구현.
 * 그 전까진 항상 false를 반환해 useOrderStatusCaa가 regularOrder 분기로만 동작.
 */
export const hasPreorderCaa = (_order: ICaaOrder | null): boolean => {
  return false;
};

/**
 * 주문이 반품 가능한 기간 내에 있는지 확인
 * @param {IOrdersEdgesNode} order - 주문
 * @param {number} refundMaxDate - 반품이 가능한 최대 일수
 * @returns {boolean} - 현재 날짜가 반품 가능 기간 내에 있으면 `true`, 그렇지 않으면 `false`를 반환
 */
export const isReturnPeriodValid = (
  order: IOrdersEdgesNode,
  refundMaxDate: number,
): boolean => {
  // fulfillmentDate로 바꾸기
  const orderDate = new Date(order.processedAt);
  const today = new Date();
  const diffInDays =
    (today.getTime() - orderDate.getTime()) / (24 * 60 * 60 * 1000);
  return diffInDays < refundMaxDate;
};

/**
 * @description 배송 tracking 정보 조회
 */
export const getTrackingInfo = ({
  order,
  lang,
}: {
  order?: IOrdersEdgesNode | null;
  lang: "ko" | "en" | "jp";
}) => {
  if (!order) return;

  const CJ_TRACKING_URL =
    "https://trace.cjlogistics.com/next/tracking.html?wblNo=";

  const fulfillment = order.successfulFulfillments;
  const info = fulfillment?.[fulfillment.length - 1]?.trackingInfo[0];
  if (info) {
    const trackingUrl =
      lang === "ko"
        ? `${CJ_TRACKING_URL}${info.number ?? ""}`
        : (info.url ?? "");
    return { ...info, url: trackingUrl };
  }

  return null;
};

/**
 * @description Gift 태그가 없는 일반 상품이 먼저, Gift 태그가 있는 상품이 나중에 정렬
 */
export const sortOrderItems = (
  items: ILineItem[] | IReturnLineItem[],
): ILineItem[] | IReturnLineItem[] => {
  return items.sort((a, b) => {
    const aIsGift = a.variant?.product.tags.includes("Gift");
    const bIsGift = b.variant?.product.tags.includes("Gift");
    return Number(aIsGift) - Number(bIsGift);
  });
};

export const RESIDENT_COUNTRY = {
  BR: { name: "CPF/CNPJ", key: "SHIPPING_CREDENTIAL_BR" },
  CN: { name: "Resident ID", key: "SHIPPING_CREDENTIAL_CN" },
  MX: { name: "RFC", key: "SHIPPING_CREDENTIAL_MX" },
};

// Resident ID /CPF/CNPJ/RFC 유효성 검사
export const checkResidentId = (
  residentId: string,
  address: MailingAddress | undefined,
): boolean => {
  const countryCode = address?.countryCodeV2 || "";
  if (!countryCode) return false;

  // 브라질
  if (countryCode === "BR") {
    const value = residentId.replace(/\D/g, "");

    // CPF
    if (value.length === 11 && residentRegex.CFP.test(value)) {
      return true;
    }

    // CNPJ
    if (value.length === 14 && residentRegex.CNPJ.test(value)) {
      return true;
    }

    return false;
  }

  // 중국
  if (countryCode === "CN") return residentRegex.CN.test(residentId);

  // 멕시코
  if (countryCode === "MX") return residentRegex.RFC.test(residentId);

  return true;
};
